import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Trophy, Target, Users, Clock, Share2, Sword, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import Avatar from "./Avatar";
import { useLanguage } from "@/contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Challenge = () => {
  const { challengeId } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const { t, language } = useLanguage();
  const getTranslatedText = (translations, fallback) => {
    if (!translations) return fallback;
    if (translations[language]) return translations[language];
    if (translations['ru']) return translations['ru'];
    return fallback;
  };
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);

  useEffect(() => {
    fetchChallenge();
  }, [challengeId]);

  const fetchChallenge = async () => {
    try {
      const response = await axios.get(`${API}/challenges/${challengeId}`);
      setChallenge(response.data);
    } catch (error) {
      console.error("Error fetching challenge:", error);
      if (error.response?.status === 404) {
        toast.error(t('challenge.challengeWasNotFound'));
      } else {
        toast.error(t('challenge.challengeLoadingError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePlayGame = () => {
    navigate(`/${challenge.game_id}`);
  };

  const handleCompleteChallenge = async () => {
    if (!user) {
      toast.error(t('challenge.logParticipateChallenge'));
      navigate("/login");
      return;
    }

    setCompleting(true);
    try {
      const response = await axios.post(
        `${API}/challenges/${challengeId}/complete`,
        {},
        { headers: getAuthHeader() }
      );
      
      if (response.data.beat_challenge) {
        toast.success(response.data.message);
      } else {
        toast.info(response.data.message);
      }
      
      // Refresh challenge data
      fetchChallenge();
    } catch (error) {
      console.error("Error completing challenge:", error);
      toast.error(error.response?.data?.detail || "Ошибка при завершении челленджа");
    } finally {
      setCompleting(false);
    }
  };

  const handleShare = () => {
    const url = `https://countryscanner.ru/game/challenge/${challengeId}`;
    const text = `Попробуй побить мой рекорд в игре "${getTranslatedText(challenge.game_name_translations, challenge.game_name)}"! Мой счёт: ${challenge.target_score.toLocaleString('ru-RU')} очков.`;
    
    if (navigator.share) {
      navigator.share({ title: 'Челлендж Country Scanner', text, url });
    } else {
      window.open(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="gallery-container">
        <div className="loading-state">{t('challenge.loading')}</div>
      </div>
    );
  }

  if (!challenge) {
    return (
      <div className="gallery-container">
        <div className="empty-state">
          <Sword size={64} strokeWidth={1} />
          <h2>{t('challenge.challengeWasNotFound2')}</h2>
          <Button onClick={() => navigate("/")} style={{ marginTop: "1rem" }}>
            {t('challenge.goMainPage')}
          </Button>
        </div>
      </div>
    );
  }

  const isExpired = challenge.expired;
  const isChallenger = user?.id === challenge.challenger_id;
  const userCompletion = challenge.completed_by?.find(c => c.user_id === user?.id);

  return (
    <div className="gallery-container" style={{ maxWidth: "800px", padding: "1.5rem" }}>
      {/* Header */}
      <div style={{ marginBottom: "1.5rem" }}>
        <Button variant="outline" onClick={() => navigate("/")} style={{ marginBottom: "1rem" }}>
          <ArrowLeft size={18} style={{ marginRight: "0.5rem" }} />
          {t('challenge.back')}
        </Button>
      </div>

      {/* Challenge Card */}
      <div style={{
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        borderRadius: "16px",
        padding: "2rem",
        color: "white",
        marginBottom: "1.5rem",
        boxShadow: "0 10px 40px rgba(102, 126, 234, 0.4)"
      }}>
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          gap: "0.75rem",
          marginBottom: "1.5rem"
        }}>
          <Sword size={32} />
          <h1 style={{ margin: 0, fontSize: "1.75rem" }}>{t('challenge.challenge')}</h1>
          {isExpired && (
            <span style={{
              background: "rgba(255,255,255,0.2)",
              padding: "0.25rem 0.75rem",
              borderRadius: "12px",
              fontSize: "0.8rem"
            }}>
              {t('challenge.expired')}
            </span>
          )}
        </div>

        {/* Challenger Info */}
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "1rem",
          marginBottom: "1.5rem",
          padding: "1rem",
          background: "rgba(255,255,255,0.1)",
          borderRadius: "12px"
        }}>
          <Avatar
            userId={challenge.challenger_id}
            avatarUrl={challenge.challenger_avatar}
            userName={challenge.challenger_name}
            size={48}
          />
          <div>
            <div style={{ fontWeight: 600, fontSize: "1.1rem" }}>
              {challenge.challenger_name}
            </div>
            <div style={{ opacity: 0.9, fontSize: "0.9rem" }}>
              {t('challenge.challenged')}
            </div>
          </div>
        </div>

        {/* Target */}
        <div style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          padding: "1.5rem",
          background: "rgba(255,255,255,0.15)",
          borderRadius: "12px",
          marginBottom: "1.5rem"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.5rem" }}>
            <Target size={24} />
            <span style={{ fontSize: "1rem", opacity: 0.9 }}>{t('challenge.beatRecord')}</span>
          </div>
          <div style={{ fontSize: "3rem", fontWeight: 700 }}>
            {challenge.target_score.toLocaleString('ru-RU')}
          </div>
          <div style={{ fontSize: "1rem", opacity: 0.9 }}>
            очков ({challenge.target_accuracy}% точности)
          </div>
        </div>

        {/* Game Info */}
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "1rem",
          background: "rgba(255,255,255,0.1)",
          borderRadius: "12px"
        }}>
          <div>
            <div style={{ opacity: 0.8, fontSize: "0.85rem" }}>{t('challenge.game')}</div>
            <div style={{ fontWeight: 600, fontSize: "1.1rem" }}>{getTranslatedText(challenge.game_name_translations, challenge.game_name)}</div>
          </div>
          <Button
            onClick={handlePlayGame}
            style={{
              background: "white",
              color: "#667eea",
              fontWeight: 600
            }}
          >
            {t('challenge.play')}
          </Button>
        </div>
      </div>

      {/* User's completion status */}
      {user && userCompletion && (
        <div style={{
          background: userCompletion.beat_challenge ? "#dcfce7" : "#fef3c7",
          border: `1px solid ${userCompletion.beat_challenge ? "#86efac" : "#fde047"}`,
          borderRadius: "12px",
          padding: "1.5rem",
          marginBottom: "1.5rem"
        }}>
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            gap: "0.75rem",
            marginBottom: "0.75rem"
          }}>
            {userCompletion.beat_challenge ? (
              <>
                <Crown size={24} style={{ color: "#22c55e" }} />
                <span style={{ fontWeight: 600, color: "#15803d", fontSize: "1.1rem" }}>
                  {t('challenge.veBrokenRecord')}
                </span>
              </>
            ) : (
              <>
                <Target size={24} style={{ color: "#ca8a04" }} />
                <span style={{ fontWeight: 600, color: "#92400e", fontSize: "1.1rem" }}>
                  {t('challenge.havenTManagedBreak')}
                </span>
              </>
            )}
          </div>
          <div style={{ color: userCompletion.beat_challenge ? "#166534" : "#78350f" }}>
            {t('challenge.result')} <strong>{userCompletion.score.toLocaleString('ru-RU')}</strong> очков
            {!userCompletion.beat_challenge && (
              <> (не хватило {challenge.target_score - userCompletion.score} очков)</>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div style={{
        display: "flex",
        flexDirection: "column",
        gap: "0.75rem",
        marginBottom: "1.5rem"
      }}>
        {user && !isChallenger && !userCompletion && (
          <Button
            onClick={handleCompleteChallenge}
            disabled={completing || isExpired}
            style={{
              background: "linear-gradient(135deg, #667eea, #764ba2)",
              color: "white",
              padding: "1rem",
              fontSize: "1rem",
              fontWeight: 600
            }}
          >
            {completing ? "Проверка..." : "Принять челлендж"}
          </Button>
        )}
        
        <Button
          onClick={handleShare}
          variant="outline"
          style={{
            padding: "1rem",
            fontSize: "1rem",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem"
          }}
        >
          <Share2 size={18} />
          {t('challenge.shareChallenge')}
        </Button>
      </div>

      {/* Leaderboard */}
      {challenge.leaderboard && challenge.leaderboard.length > 0 && (
        <div style={{
          background: "white",
          borderRadius: "12px",
          border: "1px solid #e5e5e5",
          overflow: "hidden"
        }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            padding: "1rem 1.5rem",
            borderBottom: "1px solid #e5e5e5",
            background: "#f9fafb"
          }}>
            <Users size={20} />
            <h3 style={{ margin: 0, fontSize: "1rem", fontWeight: 600 }}>
              Участники ({challenge.completed_by?.length || 0})
            </h3>
          </div>
          
          <div style={{ padding: "0.5rem 0" }}>
            {challenge.leaderboard.map((entry, index) => (
              <div
                key={entry.user_id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  padding: "0.75rem 1.5rem",
                  borderBottom: index < challenge.leaderboard.length - 1 ? "1px solid #f3f4f6" : "none"
                }}
              >
                <div style={{
                  width: "28px",
                  height: "28px",
                  borderRadius: "50%",
                  background: entry.beat_challenge ? "#dcfce7" : "#f3f4f6",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  color: entry.beat_challenge ? "#15803d" : "#6b7280"
                }}>
                  {index + 1}
                </div>
                <Avatar
                  userId={entry.user_id}
                  avatarUrl={entry.user_avatar}
                  userName={entry.user_name}
                  size={36}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 500 }}>{entry.user_name}</div>
                </div>
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem"
                }}>
                  {entry.beat_challenge && <Crown size={16} style={{ color: "#22c55e" }} />}
                  <span style={{ 
                    fontWeight: 600,
                    color: entry.beat_challenge ? "#15803d" : "#374151"
                  }}>
                    {entry.score.toLocaleString('ru-RU')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Expiry info */}
      {challenge.expires_at && !isExpired && (
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          justifyContent: "center",
          marginTop: "1.5rem",
          color: "#6b7280",
          fontSize: "0.9rem"
        }}>
          <Clock size={16} />
          <span>
            Истекает: {new Date(challenge.expires_at).toLocaleDateString('ru-RU', {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })}
          </span>
        </div>
      )}
    </div>
  );
};

export default Challenge;
